import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";

export function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking");
    const [info, setInfo] = useState(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let stop = false;
        const poll = async () => {
            if (stop) return;
            attempts.current += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                setInfo(data);
                if (data.payment_status === "paid") { setState("paid"); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
            } catch { /* keep polling */ }
            if (attempts.current >= 12) { setState("timeout"); return; }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { stop = true; };
    }, [sessionId]);

    return (
        <div className="min-h-screen grid place-items-center bg-muted/30 p-6">
            <Card className="p-10 max-w-md w-full text-center" data-testid="payment-success-card">
                {state === "checking" && <><Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" /><h1 className="mt-4 font-heading text-xl font-bold">Confirming your payment…</h1><p className="mt-2 text-sm text-foreground/60">This only takes a moment.</p></>}
                {state === "paid" && <>
                    <span className="grid h-16 w-16 mx-auto place-items-center rounded-full bg-success/15 text-success"><CheckCircle2 className="h-8 w-8" /></span>
                    <h1 className="mt-4 font-heading text-2xl font-bold">You're enrolled! 🎉</h1>
                    <p className="mt-2 text-foreground/60">Payment confirmed for <b>{info?.program_title}</b>. Your program is ready.</p>
                    <div className="mt-6 flex gap-2 justify-center">
                        <Button className="rounded-full" data-testid="start-learning-btn" onClick={() => navigate(`/learn/${info?.program_slug}`)}>Start learning</Button>
                        <Button variant="outline" className="rounded-full" onClick={() => navigate("/dashboard")}>Dashboard</Button>
                    </div>
                </>}
                {(state === "error" || state === "timeout") && <>
                    <span className="grid h-16 w-16 mx-auto place-items-center rounded-full bg-destructive/15 text-destructive"><XCircle className="h-8 w-8" /></span>
                    <h1 className="mt-4 font-heading text-xl font-bold">{state === "timeout" ? "Still processing" : "Payment issue"}</h1>
                    <p className="mt-2 text-sm text-foreground/60">{state === "timeout" ? "Your payment is taking longer than expected. Check your dashboard shortly." : "We couldn't confirm your payment. Please try again."}</p>
                    <Button className="mt-6 rounded-full" onClick={() => navigate("/dashboard")}>Go to Dashboard</Button>
                </>}
            </Card>
        </div>
    );
}

export function PaymentCancel() {
    return (
        <div className="min-h-screen grid place-items-center bg-muted/30 p-6">
            <Card className="p-10 max-w-md w-full text-center">
                <span className="grid h-16 w-16 mx-auto place-items-center rounded-full bg-muted text-foreground/50"><XCircle className="h-8 w-8" /></span>
                <h1 className="mt-4 font-heading text-xl font-bold">Payment cancelled</h1>
                <p className="mt-2 text-sm text-foreground/60">No charge was made. You can enrol whenever you're ready.</p>
                <Button asChild className="mt-6 rounded-full"><Link to="/programs">Back to Programs</Link></Button>
            </Card>
        </div>
    );
}
